import { Link, Navigate, Route, Routes } from "react-router";
import { AlertTriangle, ArrowRight, Brain } from "lucide-react";
import BookingRebookingPage from "./booking-rebooking";
import BookingPredictionsPage from "./booking-predictions";
import CapacityOptimizerPage from "./capacity-optimizer";
import BookingCalendarPage from "./booking-calendar";
import BookingSchedulePage from "./booking-schedule";
import ServiceCatalogPage from "./catalog";
import CreditsPage from "./credits";
import FieldDispatchPage from "./dispatch";
import EstimateBuilderPage from "./estimates";
import GroupSessionsPage from "./groups";
import InspectionsPage from "./inspections";
import OfferTemplateLibraryPage from "./offer-templates";
import PractitionerWorkspacePage from "./practitioner-workspace";
import ResourceManagementPage from "./resources";
import ServiceRemindersPage from "./reminders";
import VisitAutomationsPage from "./visit-automations";

export function ServicesUnavailablePage() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="rounded-2xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-[var(--color-warning)]" />
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Service delivery screens are gated</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              This dashboard shell no longer mounts service screens that still depend on dead <code>/api/services*</code> or
              mismatched <code>/api/bookings*</code> endpoints.
            </p>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Only the mounted service-delivery paths stay exposed here. Legacy surfaces that still depend on retired
              contracts stay gated until they are rebuilt on the live CRM and page-serving runtime.
            </p>
            <p className="text-sm text-[var(--color-muted-foreground)]">
          Catalog intake settings, inspections, capacity optimization, and the verified booking flows remain available.
          The customer-facing booking widget reads the same public service, availability, and booking contracts used by published pages.
          Legacy packages and the rest of the old service-delivery shells are intentionally retired
          here until their mounted contracts are rebuilt.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-ai)]">
          <Brain className="h-4 w-4" />
          Live surface
        </div>
        <h2 className="mt-3 text-lg font-semibold text-[var(--color-foreground)]">Use the mounted service tools</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Rebooking intelligence reads from <code>/crm/intelligence/bookings/rebooking</code>. The staff booking calendar
          now reads from <code>/crm/bookings/calendar</code> and uses the mounted booking, reschedule, and walk-in routes.
          Capacity optimization reads from <code>/crm/bookings/capacity</code> and uses the shared service-delivery optimizer.
          Field dispatch reads from <code>/crm/dispatch/jobs*</code> for assignment suggestions, staff assignment, and status tracking.
          Booking reminders read from the mounted <code>/crm/bookings/reminders*</code> surface and save timing through the service catalog.
          Catalog intake settings read from <code>/crm/bookings/services/intake</code> and <code>/crm/bookings/intake-forms</code>.
          Group sessions now run on <code>/crm/bookings/groups*</code> plus the mounted attendance and waitlist runtime.
          These are live CRM-backed paths, not placeholder shells.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to="/services/bookings/schedule"
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            Open Booking Widget
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/catalog"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Service Catalog
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/bookings/rebooking"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Rebooking Intelligence
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/capacity-optimizer"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Capacity Optimizer
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/dispatch"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Field Dispatch
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/bookings/reminders"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Booking Reminders
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/bookings/calendar"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Booking Calendar
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/groups"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Group Sessions
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/practitioner-workspace"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Practitioner Workspace
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services/inspections"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Open Inspections
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Services module now exposes only routes with verified backend support.
 * Mounted CRM-backed routes stay available. Dead /api/services* and mismatched /api/bookings* screens are explicitly retired.
 */
export default function ServicesPage() {
  return (
    <Routes>
      <Route index element={<Navigate to="bookings/rebooking" replace />} />
      <Route path="catalog" element={<ServiceCatalogPage />} />
      <Route path="estimates" element={<EstimateBuilderPage />} />
      <Route path="bookings/calendar" element={<BookingCalendarPage />} />
      <Route path="bookings/capacity" element={<CapacityOptimizerPage />} />
      <Route path="bookings/predictions" element={<BookingPredictionsPage />} />
      <Route path="bookings/rebooking" element={<BookingRebookingPage />} />
      <Route path="bookings/reminders" element={<ServiceRemindersPage />} />
      <Route path="bookings/schedule" element={<BookingSchedulePage />} />
      <Route path="capacity-optimizer" element={<CapacityOptimizerPage />} />
      <Route path="credits" element={<CreditsPage />} />
      <Route path="dispatch" element={<FieldDispatchPage />} />
      <Route path="groups" element={<GroupSessionsPage />} />
      <Route path="inspections" element={<InspectionsPage />} />
      <Route path="offer-templates" element={<OfferTemplateLibraryPage />} />
      <Route path="practitioner-workspace" element={<PractitionerWorkspacePage />} />
      <Route path="resources" element={<ResourceManagementPage />} />
      <Route path="visit-automations" element={<VisitAutomationsPage />} />
      <Route path="unavailable" element={<ServicesUnavailablePage />} />
      <Route path="*" element={<ServicesUnavailablePage />} />
    </Routes>
  );
}
